// React
import { Fragment, useContext, useEffect, useState } from "react"

// Components
import {
  BaseTable,
  CancelButton,
  CommandButton,
  Loading,
  PrintTransaction,
} from "components"
import { ActionButton } from "../components"
import {
  AttachmentModal,
  DeleteModal,
  InputSection,
  JournalListOnPrint,
} from "./components"

// Contexts
import { AccountProvider } from "contexts"
import { BillContext } from "./contexts"

// Form
import { yupResolver } from "@hookform/resolvers/yup"
import { FormProvider, useForm, useFormContext } from "react-hook-form"

// Third-Party Libraries
import moment from "moment"
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"

// Utils
import { useList } from "pages/Setup/Preference/SettingCompany/utils/hooks"
import {
  convertNumber,
  useApi,
  useCountryById,
  useHandleAuth,
  useProvinceById,
  useRegencyById,
  useSubdistrictById,
  useToggle,
} from "utils"
import {
  useEmployeeDetail,
  useSubdomainSetting,
  useVendorDetail,
} from "../PurchaseOrder/utils"
import { FormType, useDetail, validationSchema } from "./utils"
import { useApprovalStore } from "../utils"

export default function UpdatePaymentBillPage(): JSX.Element {
  // Hooks
  const { payment_bill_id } = useParams()

  return (
    <UpdatePage
      payment_bill_id={payment_bill_id!}
    />
  )
}

export function UpdatePage(params: {
  payment_bill_id: string
}): JSX.Element {
  // Hooks
  const { data, isLoading } = useDetail(params.payment_bill_id)
  const { setApproval, resetApproval } = useApprovalStore()

  useEffect(() => {
    resetApproval()
    setApproval(data?.approve_status || 0)
    return () => resetApproval()

  }, [data, setApproval, resetApproval])


  return (
    <BillContext.Provider
      value={data ?? {
        approve_by: null,
        approve_status: 0,
        bill_id: 0,
        coa_id: "",
        deposit_amount: 0,
        memo: "",
        payment_amount: 0,
        purchase_order_id: 0,
        referensi: "",
        transaction_date: "",
        trx_code: "",
        databill: {
          address: "",
          balance: 0,
          location_id: "",
          saldo_deposit: 0,
          trx_code: "",
          vendor_id: "",
          vendor_name: ""
        }
      }}
    >
      <AccountProvider>
        {isLoading || !data ? (
          <Loading loading={isLoading} />
        ) : (
          <FormSection payment_bill_id={params.payment_bill_id} />
        )}
      </AccountProvider>
    </BillContext.Provider>
  )
}


type FormSectionProps = {
  payment_bill_id: string;
};

function FormSection({ payment_bill_id }: FormSectionProps) {
  // Hooks
  const api = useApi()
  const data = useContext(BillContext)
  const navigate = useNavigate()

  // Vars
  const databill = data.databill
  const saldo_deposit = databill.saldo_deposit

  // Form
  const defaultValues: FormType = {
    _balance: 0,
    coa_id: data.coa_id,
    memo: data.memo,
    deposit_amount: data.deposit_amount,
    payment_amount: data.payment_amount,
    referensi: data.referensi,
    trx_code: data.trx_code,
    transaction_date: data.transaction_date,
    vendor_id: databill.vendor_id,
    imgPath: data.attachment,
  }
  const methods = useForm<FormType>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })
  const onSubmit = (value: FormType) => {
    // Vars
    const final_value = {
      payment_bill_id,
      bill_id: data.bill_id,
      trx_code: value.trx_code,
      vendor_id: value.vendor_id,
      location_id: databill.location_id,
      address: databill.address,
      memo: value.memo,
      transaction_date: value.transaction_date,
      referensi: value.referensi,
      coa_id: value.coa_id,
      deposit_amount: value.deposit_amount,
      payment_amount: value.payment_amount,
      imgPath: value.imgPath || [],
    }
    const isBelowBalance: boolean =
      databill.balance - value.deposit_amount - value.payment_amount < 0

    return new Promise<void>((resolve) => {
      // Functions
      const submitForm = (): void => {
        toast
          .promise(api.put("/paymentbill/updatepayment", final_value), {
            loading: "Loading...",
            success: (res) => res.data.message,
            error: (err) =>
              err.response.data.detail?.message ?? err.response.data.message,
          })
          .then(() => {
            navigate(-1)
          })
          .catch(() => { })
          .finally(resolve)
      }

      if (isBelowBalance) {
        toast.error(
          "Payment Deposit & Payment Amount cannot be more than Balance Bill"
        )
        return resolve()
      } else if (value.deposit_amount > saldo_deposit) {
        toast.error(
          `Payment Deposit cannot be more than The Deposit Balance (Current balance: ${convertNumber(saldo_deposit).intoNormalAmount
          })`
        )
        return resolve()
      }

      submitForm()
    })
  }

  return (
    <FormProvider {...methods}>
      <InputSection
        buttonsection={
          <ActionButton>
            <section className="flex flex-wrap gap-3">
              {data.approve_status === 4 ? (
                <Print />
              ) : (
                <CommandButton actiontype="print" />
              )}

              <AttachmentModal
                code={methods?.control?._formValues?.trx_code!}
              />
            </section>

            <section className="flex flex-wrap gap-3">
              <CancelButton />

              {data.approve_status === 1 && <Delete />}

              {data.approve_status !== 4 && (
                <CommandButton
                  actiontype="save"
                  loading={methods.formState.isSubmitting ? "true" : undefined}
                  onClick={methods.handleSubmit(onSubmit)}
                />
              )}
            </section>
          </ActionButton>
        }
      />
    </FormProvider>
  )
}

function Delete(): JSX.Element {
  // Hooks
  const navigate = useNavigate()
  const { payment_bill_id } = useParams()
  const { isActive, toggle } = useToggle(false)

  return (
    <Fragment>
      <CommandButton actiontype="delete" onClick={toggle} />

      {isActive && (
        <DeleteModal
          payment_bill_id={payment_bill_id!}
          toggle={toggle}
          onSuccess={() => navigate(-1)}
        />
      )}
    </Fragment>
  )
}

function Print(): JSX.Element {
  // Form
  const {
    formState: { defaultValues },
  } = useFormContext<FormType>()

  // Hooks
  const { approve_by } = useContext(BillContext)
  const approvedBy = useEmployeeDetail(approve_by)
  const company = useList()
  const country = useCountryById()
  const province = useProvinceById()
  const regency = useRegencyById()
  const setting = useSubdomainSetting()
  const subdistrict = useSubdistrictById()
  const vendor = useVendorDetail(defaultValues?.vendor_id)
  const { userTokenData } = useHandleAuth()
  const [address, setAddress] = useState<string>("")

  // Vars
  const vendorData = vendor.data
  const userToken = userTokenData()

  // Functions
  const getAddress = (): string => {
    let generateAddress = address ?? ""

    if (subdistrict.data) {
      generateAddress += ", " + subdistrict.data.subdistrict_name
    }

    if (regency.data) {
      generateAddress += ", " + regency.data.regency_name
    }

    if (province.data) {
      generateAddress += ", " + province.data.province_name
    }

    if (country.data) {
      generateAddress += ", " + country.data.country_name
    }

    return generateAddress
  }
  const getVendorAddress = (): string => {
    let generateAddress = vendorData?.address ?? ""

    if (vendorData?.subdistrict_name) {
      generateAddress += ", " + vendorData.subdistrict_name
    }

    if (vendorData?.regency_name) {
      generateAddress += ", " + vendorData.regency_name
    }

    if (vendorData?.province_name) {
      generateAddress += ", " + vendorData.province_name
    }

    if (vendorData?.country_name) {
      generateAddress += ", " + vendorData.country_name
    }

    return generateAddress
  }

  useEffect(() => {
    company.fetchData().then((res) => {
      setAddress(res.address)

      if (res.subdistrict_id) {
        subdistrict.fetchData(res.subdistrict_id)
      }

      if (res.regency_id) {
        regency.fetchData(res.regency_id)
      }

      if (res.province_id) {
        province.fetchData(res.province_id)
      }

      if (res.country_id) {
        country.fetchData(res.country_id)
      }
    })

    // eslint-disable-next-line
  }, [])

  return (
    <PrintTransaction
      loading={
        company.isLoading ||
        country.isLoading ||
        province.isLoading ||
        subdistrict.isLoading ||
        regency.isLoading
      }
    >
      <section className="flex flex-col gap-3">
        <div className="text-center text-2xl font-bold uppercase">
          {userToken?.company_name}
        </div>

        <section className="grid grid-cols-2 items-stretch gap-1">
          <section className="font-bold">
            <table className="table-top">
              <tbody className="font-bold">
                <tr>
                  <td className="pr-2">ADDRESS</td>
                  <td>: {getAddress()}</td>
                </tr>

                <tr>
                  <td>PHONE</td>
                  <td> : {setting.data?.phone ? setting.data?.phone : "-"}</td>
                </tr>

                <tr>
                  <td>EMAIL</td>
                  <td> : {setting.data?.email ? setting.data?.email : "-"}</td>
                </tr>

                <tr>
                  <td>WEBSITE</td>
                  <td>
                    : {setting.data?.website ? setting.data?.website : "-"}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>

          <section className="flex flex-col justify-between items-end">
            <div className="text-2xl font-bold text-right">PAYMENT BILL</div>

            <table>
              <tbody className="font-bold ">
                <tr>
                  <td width={10} className="text-right">
                    DATE :{" "}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {defaultValues?.transaction_date
                      ? moment(defaultValues?.transaction_date).format(
                        "DD/MM/YYYY"
                      )
                      : "-"}
                  </td>
                </tr>

                <tr>
                  <td width={10} className="whitespace-nowrap">
                    PAYMENT BILL NO :{" "}
                  </td>
                  <td className="whitespace-nowrap text-right">
                    {defaultValues?.trx_code}
                  </td>
                </tr>
              </tbody>
            </table>
          </section>
        </section>

        <table className="table table-bordered">
          <tbody>
            <tr className="bg-black text-white font-bold">
              <td colSpan={2}>VENDOR INFORMATION</td>
            </tr>

            <tr>
              <td colSpan={2}>
                <div>VENDOR NAME</div>
                <div className="font-bold">{vendorData?.vendor_name}</div>
              </td>
            </tr>

            <tr>
              <td colSpan={2}>
                <div>ADDRESS</div>
                <div className="font-bold">{getVendorAddress()}</div>
              </td>
            </tr>

            <tr>
              <td>
                <div>CONTACT NO</div>
                <div className="font-bold">{vendorData?.handphone}</div>
              </td>

              <td>
                <div>EMAIL ADDRESS</div>
                <div className="font-bold">{vendorData?.email}</div>
              </td>
            </tr>
          </tbody>
        </table>

        <BaseTable>
          <thead>
            <tr className="bg-black text-white font-bold">
              <td colSpan={5}>JOURNAL ENTRY</td>
            </tr>
            <tr>
              <th>ACCOUNT</th>
              <th>DEBET</th>
              <th>CREDIT</th>
            </tr>
          </thead>

          <tbody>
            <JournalListOnPrint.PaymentAccount />
            <JournalListOnPrint.PaymentDeposit />
          </tbody>
        </BaseTable>

        <section className="grid grid-cols-5 items-stretch gap-5">
          <section className="col-span-2">
            <table className="table table-bordered">
              <tbody>
                <tr className="bg-black text-white font-bold">
                  <td>Additional Notes:</td>
                </tr>

                <tr className="min-h-[300px]">
                  <td>{defaultValues?.memo}</td>
                </tr>
              </tbody>
            </table>
          </section>
          <div />

          <section className="col-span-2">
            <table className="w-full">
              <tbody>
                <tr className="bg-black text-white font-bold">
                  <td>Authorized By</td>
                </tr>

                <tr className="h-[100px]" />

                <tr>
                  <td>{approvedBy.data?.employee_name}</td>
                </tr>

                <tr>
                  <td>{approvedBy.data?.job_position_name}</td>
                </tr>
              </tbody>
            </table>
          </section>
        </section>
      </section>
    </PrintTransaction>
  )
}
